import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { money, fmtDate } from "@/lib/api";
import { Spinner } from "@/components/shared";
import { ArrowLeft, Printer, Store } from "lucide-react";

export default function Statement() {
  const { lineId } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => { api.get(`/settlements/lines/${lineId}/statement`).then((r) => setD(r.data)); }, [lineId]);
  if (!d) return <Spinner />;

  const cur = d.store.currency || "USD";
  const l = d.line, s = d.settlement;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 no-print">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="statement-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button data-testid="print-statement-btn" onClick={() => window.print()}
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg text-sm">
          <Printer className="w-4 h-4" /> Print / Save PDF
        </button>
      </div>

      <div className="printable-statement bg-white border border-slate-200 rounded-xl shadow-lg p-8 sm:p-12 max-w-4xl mx-auto text-slate-900" data-testid="statement-doc">
        <div className="flex items-center justify-between border-b-2 border-slate-900 pb-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-emerald-700 flex items-center justify-center"><Store className="w-6 h-6 text-white" /></div>
            <div>
              <p className="font-display text-xl font-extrabold tracking-tight">CONSIGNOUT</p>
              <p className="text-sm text-slate-500">{d.store.name}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-display text-lg font-bold">{s.label}</p>
            <p className="text-sm text-slate-500">Consignor Settlement</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-8">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Consignor</p>
            <p className="font-bold text-lg">{l.consignor_name}</p>
            {d.consignor?.email && <p className="text-sm text-slate-500">{d.consignor.email}</p>}
            {d.consignor?.phone && <p className="text-sm text-slate-500">{d.consignor.phone}</p>}
          </div>
          <div className="sm:text-right">
            <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Items Sold</p>
            <p className="font-mono-fin font-bold text-lg">{l.items_sold}</p>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl p-6 mb-8">
          {[["Gross Sales", money(l.gross_sales, cur)], ["Discounts", `-${money(l.discounts, cur)}`],
            ["Returns", money(l.returns, cur)], ["Net Eligible Sales", money(l.net_sales, cur)],
            ["Consignor Share", `${l.payout_pct}%`], l.adjustment ? ["Manual Adjustment", money(l.adjustment, cur)] : null].filter(Boolean).map(([k, v]) => (
            <div key={k} className="flex justify-between py-1.5 text-sm border-b border-slate-200 last:border-0">
              <span className="text-slate-600">{k}</span><span className="font-mono-fin font-medium">{v}</span>
            </div>
          ))}
          {l.adjustment_reason && <p className="text-xs text-slate-400 italic mt-2">Adjustment: {l.adjustment_reason}</p>}
          <div className="flex justify-between items-center mt-4 pt-4 border-t-2 border-slate-900">
            <span className="font-display font-extrabold text-lg">AMOUNT OWED</span>
            <span className="font-mono-fin font-extrabold text-2xl text-emerald-700">{money(l.amount_owed, cur)}</span>
          </div>
        </div>

        <table className="w-full text-sm mb-8">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-200">
            <th className="py-2">Product</th><th className="py-2">Sale Date</th><th className="py-2 text-right">Sale Price</th>
            <th className="py-2 text-right">Discount</th><th className="py-2 text-right">Net</th><th className="py-2 text-right">Consignor Share</th></tr></thead>
          <tbody>
            {(l.details || []).map((it, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2">{it.product_name}</td><td className="py-2 text-slate-500">{fmtDate(it.sale_date)}</td>
                <td className="py-2 text-right font-mono-fin">{money(it.sale_price, cur)}</td>
                <td className="py-2 text-right font-mono-fin text-rose-600">{it.discount ? `-${money(it.discount, cur)}` : "—"}</td>
                <td className="py-2 text-right font-mono-fin">{money(it.net, cur)}</td>
                <td className="py-2 text-right font-mono-fin font-semibold">{money(it.consignor_share, cur)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex flex-wrap justify-between text-xs text-slate-400 border-t border-slate-200 pt-4">
          <span>Settlement ID: {s.id.slice(0, 8).toUpperCase()}</span>
          <span>Period: {fmtDate(s.start_date)} – {fmtDate(s.end_date)}</span>
          <span>Generated: {fmtDate(new Date().toISOString())}</span>
        </div>
      </div>
    </div>
  );
}
